<!-- src/views/MarketplaceView.vue -->
<template>
  <div class="p-6">
    <!-- Header -->
    <div class="header-row">
      <h1 class="text-2xl font-bold">Tool Marketplace</h1>
    </div>

    <!-- Category Tabs -->
    <div v-if="!store.loading && !store.error" class="tabs-row">
      <BaseButton
        v-for="cat in categories"
        :key="cat"
        type="button"
        :variant="activeCategory === cat ? 'primary' : 'secondary'"
        @click="selectCategory(cat)"
      >
        {{ cat }}
      </BaseButton>
    </div>

    <!-- Search Bar -->
    <div class="search-row">
      <BaseInput
        v-model="searchTerm"
        placeholder="Search tools..."
        class="w-full"
        @keydown.enter.prevent
      />
    </div>

    <!-- Loading & Error -->
    <div v-if="store.loading" class="loading-row text-center py-10">
      Loading tools…
    </div>
    <div v-else-if="store.error" class="error-row text-red-600">
      {{ store.error }}
    </div>

    <!-- Tools Grid -->
    <div v-else class="tools-grid">
      <ToolCard
        v-for="tool in paginatedTools"
        :key="tool.id || tool.launchRoute || tool.name"
        :tool="tool"
        @launch="() => confirmLaunch(tool)"
      />
      <div
        v-if="!filteredTools.length"
        class="no-tools-row col-span-full text-center text-gray-500"
      >
        No tools found for “{{ searchTerm }}” in “{{ activeCategory }}”
      </div>
    </div>

    <!-- Pagination Controls -->
    <div
      v-if="totalPages > 1"
      class="pagination-row flex justify-center items-center"
    >
      <button
        @click="prevPage"
        :disabled="currentPage === 1"
        class="px-3 py-1 rounded border hover:bg-gray-100 disabled:opacity-50 mr-2"
      >
        Prev
      </button>
      <button
        v-for="page in totalPages"
        :key="page"
        @click="goToPage(page)"
        :class="[
          'px-3 py-1 rounded border hover:bg-gray-100 mx-1',
          currentPage === page
            ? 'bg-blue-500 text-white border-blue-500'
            : 'bg-white text-gray-700'
        ]"
      >
        {{ page }}
      </button>
      <button
        @click="nextPage"
        :disabled="currentPage === totalPages"
        class="px-3 py-1 rounded border hover:bg-gray-100 disabled:opacity-50 ml-2"
      >
        Next
      </button>
    </div>

    <!-- Launch Confirmation Modal (paid only) -->
    <LaunchModal
      v-if="selected && Number(selected.cost) > 0"
      :tool="selected"
      @confirm="doLaunch"
      @cancel="selected = null"
    />
  </div>
</template>

<script>
import { ref, onMounted, computed, watch } from 'vue';
import { useMarketplaceStore } from '@/stores/marketplace';
import { useAuthStore } from '@/stores/auth';
import ToolCard from '@/components/marketplace/ToolCard.vue';
import LaunchModal from '@/components/marketplace/LaunchModal.vue';
import BaseInput from '@/components/ui/BaseInput.vue';
import BaseButton from '@/components/ui/BaseButton.vue';
import router from '@/router';

export default {
  name: 'MarketplaceView',
  components: { BaseInput, BaseButton, ToolCard, LaunchModal },

  setup() {
    const store = useMarketplaceStore();
    const auth  = useAuthStore();

    const searchTerm     = ref('');
    const activeCategory = ref('All');
    const selected       = ref(null);

    const pageSize    = ref(9);
    const currentPage = ref(1);

    onMounted(() => store.fetchTools());

    // Build category list from tools; include "All" first
    const categories = computed(() => {
      const cats = store.tools.map(t => (t.category || 'Uncategorized').trim());
      const unique = Array.from(new Set(cats)).sort((a, b) => a.localeCompare(b));
      return ['All', ...unique];
    });

    // Filter by category + search (name + description)
    const filteredTools = computed(() => {
      let list = store.tools;

      if (activeCategory.value !== 'All') {
        list = list.filter(t => (t.category || 'Uncategorized').trim() === activeCategory.value);
      }

      const q = (searchTerm.value || '').trim().toLowerCase();
      if (q) {
        list = list.filter(t =>
          (t.name || '').toLowerCase().includes(q) ||
          (t.description || '').toLowerCase().includes(q)
        );
      }

      return list;
    });

    // Pagination
    const totalPages = computed(() =>
      Math.max(1, Math.ceil(filteredTools.value.length / pageSize.value))
    );

    const paginatedTools = computed(() => {
      const start = (currentPage.value - 1) * pageSize.value;
      return filteredTools.value.slice(start, start + pageSize.value);
    });

    // Reset page on filters change
    watch([filteredTools, activeCategory, searchTerm], () => {
      currentPage.value = 1;
    });

    function goToPage(page) {
      if (page >= 1 && page <= totalPages.value) currentPage.value = page;
    }
    function prevPage() { goToPage(currentPage.value - 1); }
    function nextPage() { goToPage(currentPage.value + 1); }

    function selectCategory(cat) {
      activeCategory.value = cat;
    }

    // Launch flow
    async function confirmLaunch(tool) {
      // Free tools launch directly
      if (!tool?.isPaid || Number(tool.cost) <= 0) {
        return doLaunch(tool);
      }
      selected.value = tool;
    }

    async function doLaunch(tool) {
      try {
        const { launchRoute, launchUrl } = await store.launchTool(tool);

        // Prefer server-provided route
        const route = launchRoute || tool.launchRoute || '';
        if (route) {
          return router.push(route);
        }
        // External URL fallback
        if (launchUrl) {
          return window.open(launchUrl, '_blank');
        }
        // Hard fallback for GST tool
        if ((tool.name || '').toLowerCase().includes('invoice') || tool.launchRoute === '/tool/gst-invoice') {
          return router.push('/tool/gst-invoice');
        }
        alert('Unable to launch tool.');
      } catch (err) {
        alert(err?.message || 'Error launching tool');
      } finally {
        selected.value = null;
      }
    }

    return {
      store,
      auth,
      categories,
      activeCategory,
      searchTerm,
      filteredTools,
      paginatedTools,
      totalPages,
      currentPage,
      selectCategory,
      goToPage,
      prevPage,
      nextPage,
      selected,
      confirmLaunch,
      doLaunch
    };
  }
};
</script>

<style scoped>
.header-row {
  margin-bottom: 1.5rem;
}
.tabs-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
}
.search-row {
  margin-bottom: 1.5rem;
}
.loading-row,
.error-row {
  margin-bottom: 1.5rem;
}
.tools-grid {
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  gap: 1.5rem;
}
@media (min-width: 640px) {
  .tools-grid { grid-template-columns: repeat(2, 1fr); }
}
@media (min-width: 1024px) {
  .tools-grid { grid-template-columns: repeat(3, 1fr); }
}
.pagination-row {
  margin-top: 1.5rem;
}
</style>
